import {
  ACTIVE_STATUSES,
  HOLDING_TYPE,
  JOINT_CLIENT_HOLDING_PATTERNS,
  NRI_TAX_STATUSES,
} from "../constants";
import { Client, ClientHoldingPattern, ClientStatus } from "../types";

export function isActiveClient<T extends { status: ClientStatus }>(client?: T) {
  if (!client) return false;
  return ACTIVE_STATUSES.includes(client.status);
}

export function hasCresoAccount(client?: Client) {
  if (!client) return false;
  if (isActiveClient(client)) return true;
  return (
    client?.status === "SETUP_PENDING" || client?.status === "UNDER_REVIEW"
  );
}

export function isNRI(client?: Client) {
  if (!client) return false;
  return NRI_TAX_STATUSES.has(client.tax_status?.toLocaleLowerCase() ?? "");
}

export function getTaxStatusLabel(client?: Client) {
  const taxStatus = client?.tax_status?.toLocaleLowerCase();
  if (!taxStatus) return "Individual";
  if (taxStatus === "resident_individual") return "Individual";
  if (taxStatus.includes("nri")) return "NRI";
  if (taxStatus.includes("minor")) return "Minor";
  if (taxStatus.includes("huf")) return "HUF";
  return "Individual";
}

export function enableUCCModification(client?: Client) {
  if (client?.tax_status !== "resident_individual") {
    return false;
  }
  if (client?.status === "AUTHENTICATION_PENDING") {
    return false;
  }
  return true;
}

// The "Joint" holding filter covers both joint and anyone_or_survivor
// accounts: its count adds both, and selecting it filters by both.
export function getHoldingPatternFilterCount(
  counts: Partial<Record<ClientHoldingPattern, number>> | undefined,
  value: string
) {
  if (value === "joint") {
    return JOINT_CLIENT_HOLDING_PATTERNS.reduce(
      (sum, pattern) => sum + (counts?.[pattern] ?? 0),
      0
    );
  }
  return counts?.[value as ClientHoldingPattern] ?? 0;
}

// Saved filters from older sessions may hold "anyone_or_survivor" for the
// Joint option, so map them onto the current options.
export function normalizeHoldingPatternFilter(selected: string[] = []) {
  const options = HOLDING_TYPE.map((ht) => ht.value);
  return [
    ...new Set(
      selected.map((value) =>
        JOINT_CLIENT_HOLDING_PATTERNS.includes(value as ClientHoldingPattern)
          ? "joint"
          : value
      )
    ),
  ].filter((value) => options.includes(value));
}

export function getHoldingPatternFilterValues(selected: string[] = []) {
  return [
    ...new Set(
      selected.flatMap((value) =>
        value === "joint" ? JOINT_CLIENT_HOLDING_PATTERNS : [value]
      )
    ),
  ];
}
